import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { debounce } from "throttle-debounce";

const DigitalPayButton = (props) => {
  const {
    country,
    createPaymentRequest,
    currencyCode,
    orderTotal,
    loading,
    checkIsOrderCompleteOnClick,
    submitPayment,
  } = props;

  const [paymentRequest, setPaymentRequest] = useState(null);
  const [canMakePayment, setCanMakePayment] = useState(false);
  const [isApplePay, setIsApplePay] = useState(false);
  const [isGooglePay, setIsGooglePay] = useState(false);
  const [prevOrderTotal, setPrevOrderTotal] = useState(0);
  const latestGetPaymentRequest = useRef();

  const getPaymentRequest = useCallback(() => {
    setCanMakePayment(false);

    const orderTotalInCents = orderTotal
      ? parseFloat(orderTotal.times(100))
      : 0;
    const paymentRequest = createPaymentRequest({
      country,
      currency: currencyCode.toLowerCase(),
      total: {
        amount: orderTotalInCents,
        label: "Total",
      },
    });
    paymentRequest.canMakePayment().then((result) => {
      const canMakePayment = !!result;
      setCanMakePayment(canMakePayment);
      setIsApplePay(result ? result.applePay : false);
      setIsGooglePay(result ? result.googlePay : false);
    });
    setPaymentRequest(paymentRequest);
  }, [country, createPaymentRequest, currencyCode, orderTotal]);

  const setPaymentRequestDebounced = useMemo(() => {
    return debounce(500, () => {
      latestGetPaymentRequest.current();
    });
  }, []);

  useEffect(() => {
    latestGetPaymentRequest.current = getPaymentRequest;
  }, [getPaymentRequest]);

  useEffect(() => {
    if (parseFloat(orderTotal) !== parseFloat(prevOrderTotal)) {
      setPaymentRequestDebounced();
      setPrevOrderTotal(orderTotal);
    }
  }, [orderTotal, setPaymentRequestDebounced, prevOrderTotal]);

  const preventDoubleClick = useRef(false); // digital pay button seems to allow double-click behavior so we maintain this ref to prevent a double payment attempt
  const onClick = useCallback(
    (event) => {
      event.preventDefault();

      if (preventDoubleClick.current) return;
      preventDoubleClick.current = true;
      setTimeout(() => (preventDoubleClick.current = false), 500); // prevent click for 500ms to avoid double-click

      const isOrderComplete = checkIsOrderCompleteOnClick();
      if (isOrderComplete) {
        paymentRequest.show();
        // assigning the submit method here (vs on creating the paymentRequest) so it holds the most recent order details (aside from order total)
        paymentRequest.once("paymentmethod", (event) => {
          const { paymentMethod, complete } = event;
          submitPayment(paymentMethod, complete);
        });
        // remove "paymentmethod" event listener on payment dialog cancellation
        paymentRequest.once("cancel", () => {
          paymentRequest.off("paymentmethod");
        });
      }
    },
    [checkIsOrderCompleteOnClick, paymentRequest, submitPayment]
  );

  return paymentRequest ? (
    <div>
      {isGooglePay && (
        <button
          className="btn pay-btn flex flex-align-center flex-justify-center"
          disabled={loading || !canMakePayment}
          onClick={onClick}
        >
          {loading ? "Processing..." : "Pay Now"}
          <i className="material-icons">keyboard_arrow_right</i>
        </button>
      )}
      {isApplePay && (
        <div
          class="apple-pay-button apple-pay-button-white-with-line"
          onClick={onClick}
          disabled={loading || !canMakePayment}
        />
      )}
    </div>
  ) : null;
};

export default React.memo(DigitalPayButton);
